import { Component, computed, ElementRef, OnDestroy, OnInit, Renderer2, Signal, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { RouterLink, RouterOutlet } from '@angular/router';
import { selectActiveMediaQueries, selectMediaQueryIsActive } from '@app/store/reducers';
import { Privileges, User } from '@cco/model';
import { auth2Actions } from '@core/auth2/store/auth2.actions';
import { selectAuthenticated } from '@core/auth2/store/auth2.reducer';
import { selectSessionReady } from '@core/auth2/store/session.reducer';
import * as Sentry from '@sentry/browser';
import { merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, share, take, takeUntil, tap } from 'rxjs/operators';
import { MediaStateSerializerService } from '../../services/media-state-serializer.service';
import { Go } from '../../store/actions/router.actions';
import { APP_CONFIG, AppConfig } from '@cco-apps/cco-frontend';
import { StandardContainerComponent } from '@app/shared/containers/standard-container/standard-container.component';
import { ENTITY_SERVICE, EntityService } from '@core/services/entity.service';
import {
  VolumeCalculatorComponent,
} from '@modules/volume-calculator/components/volume-calculator/volume-calculator.component';
import { ServiceWorkerUpdateService } from '@app/core/services/service-worker-update.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  UpdateAvailableSnackComponent,
} from '@app/core/components/update-available-snack/update-available-snack.component';
import {
  ErrorReportingDialogComponent,
  ErrorReportingDialogData,
  ErrorReportingDialogResult,
} from '@modules/error-reporting-dialog/error-reporting-dialog.component';
import { notNullish } from '@app/shared/rxjs/operators/standard-operators';
import { BreakpointObserver } from '@angular/cdk/layout';
import { StatusBarComponent } from '../../components/status-bar/status-bar.component';
import { SideNavMenuItemComponent } from '../side-nav-menu-item/side-nav-menu-item.component';
import { SideNavExpandableMenuComponent } from '../side-nav-expandable-menu/side-nav-expandable-menu.component';
import { MatTooltip } from '@angular/material/tooltip';
import {
  ConnectionMonitorComponent,
} from '@modules/connection-monitor/containers/connection-monitor/connection-monitor.component';
import { MatDivider } from '@angular/material/divider';
import {
  IfExternalAccountingEnabledDirective,
} from '@modules/accounting/if-external-accounting-enabled/directives/if-external-accounting-enabled.directive';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { IfHasPrivilegesDirective } from '@modules/auth-utils/directives/if-has-privileges.directive';
import { MatTabLink, MatTabNav, MatTabNavPanel } from '@angular/material/tabs';
import { BreadcrumbsComponent } from '../breadcrumbs/breadcrumbs.component';
import { MatIcon } from '@angular/material/icon';
import { MatAnchor, MatButton, MatIconAnchor, MatIconButton } from '@angular/material/button';
import { MatToolbar } from '@angular/material/toolbar';
import { AsyncPipe } from '@angular/common';
import { selectCurrentUser } from '@core/auth2/store/auth.selectors';
import { userTenantFeature } from '@core/user-tenant/user-tenant.reducer';
import {
  ContextualHelpPanelComponent,
} from '@core/components/help-panel/contextual-help-panel/contextual-help-panel.component';
import {
  ContextualHelpPanelTriggerComponent,
} from '@core/components/help-panel/contextual-help-panel-trigger/contextual-help-panel-trigger.component';
import { ContextualHelpPanelService } from '@core/services/contextual-help-panel.service';
import { GlobalDateFilterDialogService } from '@core/components/global-date-filter-dialog/global-date-filter-dialog.service';

@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    MatToolbar,
    MatAnchor,
    RouterLink,
    MatIcon,
    MatIconAnchor,
    BreadcrumbsComponent,
    MatTabNav,
    MatTabLink,
    IfHasPrivilegesDirective,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    IfExternalAccountingEnabledDirective,
    MatDivider,
    ConnectionMonitorComponent,
    MatButton,
    MatIconButton,
    MatTooltip,
    MatSidenavContainer,
    MatSidenav,
    SideNavExpandableMenuComponent,
    SideNavMenuItemComponent,
    MatSidenavContent,
    MatTabNavPanel,
    RouterOutlet,
    StatusBarComponent,
    ContextualHelpPanelTriggerComponent,
    ContextualHelpPanelComponent,
    AsyncPipe
]
})
export class LayoutComponent extends StandardContainerComponent implements OnInit, OnDestroy {
  dialog = inject(MatDialog);
  protected mediaStateSerializer = inject(MediaStateSerializerService);
  protected renderer = inject(Renderer2);
  protected entityService = inject<EntityService>(ENTITY_SERVICE);
  private updateService = inject(ServiceWorkerUpdateService);
  protected snackBar = inject(MatSnackBar);
  private breakpointObserver = inject(BreakpointObserver);
  protected helpPanelService = inject(ContextualHelpPanelService);
  protected globalDateFilterDialogService = inject(GlobalDateFilterDialogService);

  @ViewChild('mainLayout') mainLayoutEl: ElementRef<HTMLDivElement>;
  @ViewChild('helpSidenav') helpSidenav: MatSidenav;
  @ViewChild('sidenavToggleBar') helpSidenavToggleDiv: ElementRef<HTMLDivElement>;

  readonly appName: string;
  readonly title: string;
  readonly apiScalarUrl: string;

  currentUser: Signal<User>;
  givenName: Signal<string>;

  authenticated: boolean;
  authenticated$: Observable<boolean>;

  sidenavOpened = false;
  showBackNavButton$: Observable<boolean>;
  backButtonLabel$: Observable<string>;
  breadcrumbSegments$: Observable<number>;

  isMobile = false;

  panRight$ = new Subject<any>();
  panEndSource$ = new Subject<any>();
  panEnd$ = this.panEndSource$.pipe(share());
  swipeRight$ = new Subject<any>();
  panDown$ = new Subject<any>();
  swipeDown$ = new Subject<any>();

  protected readonly userHasAccessToMoreThanOneTenant = this.store.selectSignal(userTenantFeature.selectUserHasAccessToMoreThanOneTenant);

  constructor() {
    const config = inject<AppConfig>(APP_CONFIG);

    super();

    this.appName = config.appName;
    this.title = config.appName;
    this.apiScalarUrl = joinUrl(config.apiBaseUrl, 'scalar');

    this.store.select(selectMediaQueryIsActive({ mediaQuery: 'lt-md' })).pipe(
        takeUntil(this.destroyed$),
        map(active => (this.isMobile = active)),
      )
      .subscribe();

    this.currentUser = this.store.selectSignal(selectCurrentUser);
    this.givenName = computed(() => this.currentUser()?.givenName);
  }

  ngOnInit() {
    this.authenticated$ = this.store.select(selectAuthenticated);
    this.authenticated$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(authenticated => (this.authenticated = authenticated));
    this.breadcrumbSegments$ = this.store.select(selectActiveMediaQueries).pipe(
      takeUntil(this.destroyed$),
      map((activeMediaQueries) => {
        if (activeMediaQueries.includes('xs')) {
          return 2;
        }

        if (activeMediaQueries.includes('sm')) {
          return 3;
        }

        if (activeMediaQueries.includes('md')) {
          return 4;
        }

        return null;
      }),
    );
    // This layout also wraps the `/app/auth2` login routes, so wait for an
    // authenticated session rather than firing the load on a timer.  See #2861.
    this.store.select(selectSessionReady)
      .pipe(
        filter(Boolean),
        take(1),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => this.entityService.loadIfOlderThan('Customer', 'P1D'));

    // Handle swiping to the right to navigate back
    merge(
      this.panRight$,
      this.panEnd$,
      this.swipeRight$.pipe(
        tap(() => {
          let node = this.route.snapshot;
          while (node.firstChild) {
            node = node.firstChild;
          }
          if (node.data && node.data.backPath) {
            this.back(node.data.backPath);
          } else {
            this.back();
          }
        }),
      ),
    )
      .pipe(
        tap((gesture) => {
          if (this.mainLayoutEl) {
            const nativeElement = this.mainLayoutEl.nativeElement;

            if (gesture.type === 'panend' || gesture.type === 'swiperight') {
              this.renderer.removeStyle(nativeElement, 'transform');
            } else if (gesture.type === 'panright') {
              this.renderer.setStyle(nativeElement, 'transform', `translate(${gesture.deltaX}px)`);
            }
          }
        }),
      )
      .subscribe();

    // Handle swiping down to refresh
    merge(this.panDown$, this.panEnd$, this.swipeDown$).subscribe();
    /*
    this.showBackNavButton$ = this.store.pipe(
      select(selectRouterRouteData),
      takeUntil(this.destroyed$),
      map(routeData => {
        for (let i = 0; i < routeData.length; i++) {
          if (routeData[i].showBackButton) {
            return true;
          }
        }

        return false;
      }),
      shareReplay()
    );
    */
    /*
    this.backButtonLabel$ = this.store.pipe(
      select(selectRouterRouteData),
      takeUntil(this.destroyed$),
      map(routeData => {
        const leafRouteData = routeData[routeData.length - 1];
        return leafRouteData && leafRouteData.backLabel;
      })
    );
    this.label$ = this.store.pipe(
      select(selectRouterRouteData),
      takeUntil(this.destroyed$),
      map(routeData => {
        const leafRouteData = routeData[routeData.length - 1];
        return leafRouteData && leafRouteData.label;
      })
    );
    */
    // this.routingLabels$.subscribe();
    /*
    fromEvent(window, 'scroll').pipe(
      takeUntil(this.destroyed$),
      throttleTime(10),
      map(() => window.pageYOffset),
      pairwise(),
      tap(p => console.log(p))
    ).subscribe();
    */

    this.updateService.eventLog$.pipe(
      map(eventLog => eventLog.findIndex(event => event.type === 'VERSION_READY') !== -1),
      distinctUntilChanged(),
    ).subscribe((updateAvailable) => {
      if (updateAvailable) {
        this.snackBar.openFromComponent(UpdateAvailableSnackComponent).onAction().pipe(takeUntil(this.destroyed$)).subscribe(() => this.updateService.updateApp());
      }
    });
  }

  openVolumeCalculatorDialog() {
    this.dialog.open(VolumeCalculatorComponent);
  }

  protected onGlobalDateFilter() {
    void this.globalDateFilterDialogService.open();
  }

  checkCorrelationId(prev: any, cur: any): boolean {
    return prev.payload.correlationId === cur.payload.correlationId;
  }

  ngOnDestroy() {
    this.destroyed$.next();
  }

  toggleSidenav() {
    this.sidenavOpened = !this.sidenavOpened;
  }

  closeSidenav() {
    this.sidenavOpened = false;
  }

  isLinkActive(linkName: string) {
    const snapshot = this.route.snapshot;
    if (
      snapshot.firstChild &&
      snapshot.firstChild.url[0] &&
      snapshot.firstChild.url[0].path === linkName
    ) {
      return true;
    }

    return false;
  }

  onLogin() {
    this.store.dispatch(auth2Actions.authenticateUser());
  }

  onLogout() {
    this.store.dispatch(auth2Actions.logout());
    // this.store.dispatch(Go({ path: [ '/app/auth2/logout' ]}));
  }

  printPage() {
    window.print();
  }

  protected async onAbout() {
    const { AboutDialogComponent } = await import('@modules/about-dialog/components/about-dialog/about-dialog.component');
    this.dialog.open(AboutDialogComponent);
  }

  async onLearning(): Promise<void> {
    const { LearnDialogComponent } = await import('@modules/learn/components/learn-dialog/learn-dialog.component');
    this.dialog.open(LearnDialogComponent);
  }

  back(commands: any[] = ['..']) {
    let leaf = this.route;
    while (leaf.firstChild) {
      leaf = leaf.firstChild;
    }
    this.store.dispatch(Go({ path: commands, extras: { relativeTo: leaf } }));
  }

  onSwipeRight($event) {
    if (this.isMobile) {
      this.swipeRight$.next($event);
    }
  }

  onPanRight($event) {
    if (this.isMobile) {
      this.panRight$.next($event);
    }
  }

  onPanEnd($event) {
    if (this.isMobile) {
      this.panEndSource$.next($event);
    }
  }

  onPanDown($event) {
    if (this.isMobile) {
      this.panDown$.next($event);
    }
  }

  onSwipeDown($event) {
    if (this.isMobile) {
      this.swipeDown$.next($event);
    }
  }

  showHelpSidenav() {
    if (this.helpSidenav) {
      this.helpSidenav.open();
    }
    if (this.helpSidenavToggleDiv) {
      this.helpSidenavToggleDiv.nativeElement.style.display = 'none';
    }
  }

  hideHelpSidenav() {
    if (this.helpSidenav) {
      this.helpSidenav.close();
    }
    if (this.helpSidenavToggleDiv) {
      this.helpSidenavToggleDiv.nativeElement.style.display = 'block';
    }
  }

  reportIssue() {
    const dialogRef = this.dialog.open<
      ErrorReportingDialogComponent,
      ErrorReportingDialogData,
      ErrorReportingDialogResult
    >(ErrorReportingDialogComponent, { data: {} });
    dialogRef.afterClosed()
      .pipe(notNullish())
      .subscribe((result) => {
        const eventId = Sentry.captureMessage('user-reported-error');
        Sentry.captureFeedback({
          associatedEventId: eventId,
          name: '',
          email: '',
          message: result.description,
        });
      });
  }

  protected readonly Privileges = Privileges;
}

function joinUrl(base: string, path: string, origin = window.location.origin) {
  const b = base.endsWith('/') ? base : base + '/';
  const baseUrl = new URL(b, origin);
  const p = path.startsWith('/') ? path.slice(1) : path;
  return new URL(p, baseUrl).href;
}
